import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  Bell, X, Check, CheckCheck, Trash2, Filter, ChevronDown, ChevronRight,
  AlertOctagon, AlertTriangle, Info, CheckCircle2, Award, Calendar,
  Mail, MessageSquare, Archive, Stethoscope, Users, Layers, ShieldCheck, Zap
} from "lucide-react";

// Generator to build notifications from actual runtime data
export const generateExecutiveNotifications = (applications = [], stats = {}) => {
  const notifications = [];
  const now = new Date();

  // Helper to format ISO or relative string
  const formatTimeAgo = (minutesAgo) => {
    if (minutesAgo < 60) return `${minutesAgo} mins ago`;
    const hours = Math.floor(minutesAgo / 60);
    if (hours < 24) return `${hours} hrs ago`;
    const days = Math.floor(hours / 24);
    return `${days} days ago`;
  };

  // 1. Process Candidate Notifications
  applications.forEach((app, idx) => {
    const cqi = app.cqi_score || 85;
    const curr = parseFloat(String(app.current_ctc || "0").replace(/[^0-9.]/g, "")) || 0;
    const exp = parseFloat(String(app.expected_ctc || "0").replace(/[^0-9.]/g, "")) || 0;
    const growth = (curr > 0 && exp > 0) ? ((exp - curr) / curr) * 100 : 0;

    // High Priority Candidate
    if (app.priority_score === "Urgent" || app.priority_score === "High" || cqi >= 90) {
      notifications.push({
        id: `notif-cand-priority-${app.id || idx}`,
        category: "Recruitment",
        priority: "🔴 Critical",
        title: "Priority A+ Candidate Applied",
        description: `${app.full_name} (${app.position_title || app.job_role || 'Executive'}) registered with CQI score ${cqi}/100.`,
        timestamp: "Today, 09:30 AM",
        minutesAgo: 45,
        timeGroup: "Today",
        module: "Recruitment Workspace",
        navPath: "/admin/applications",
        read: false,
        icon: Award,
        iconColor: "text-red-400"
      });
    }

    // Compensation Outlier
    if (growth > 80) {
      notifications.push({
        id: `notif-cand-ctc-${app.id || idx}`,
        category: "Recruitment",
        priority: "🟠 High",
        title: "Compensation Expectation Outlier",
        description: `${app.full_name} requested +${growth.toFixed(0)}% CTC increase (${app.current_ctc} → ${app.expected_ctc}). Review compensation justification.`,
        timestamp: "Today, 11:15 AM",
        minutesAgo: 120,
        timeGroup: "Today",
        module: "Recruitment Workspace",
        navPath: "/admin/applications",
        read: false,
        icon: AlertTriangle,
        iconColor: "text-amber-400"
      });
    }

    // Joined or Selected
    if (app.status === "joined") {
      notifications.push({
        id: `notif-cand-joined-${app.id || idx}`,
        category: "Recruitment",
        priority: "🟢 Success",
        title: "Candidate Officially Joined",
        description: `${app.full_name} has accepted offer and joined the ${app.department || 'Quantitative Strategies'} department.`,
        timestamp: "Yesterday, 16:45 PM",
        minutesAgo: 1440,
        timeGroup: "Yesterday",
        module: "Recruitment Workspace",
        navPath: "/admin/applications",
        read: true,
        icon: CheckCircle2,
        iconColor: "text-emerald-400"
      });
    }
  });

  // 2. Upcoming Interview Alerts
  notifications.push({
    id: "notif-interview-today-1",
    category: "Interviews",
    priority: "🟠 High",
    title: "Executive Technical Panel Scheduled Today",
    description: "Quantitative Portfolio Manager evaluation round starts at 14:00 IST via Microsoft Teams.",
    timestamp: "Today, 10:00 AM",
    minutesAgo: 60,
    timeGroup: "Today",
    module: "Interview Workspace",
    navPath: "/admin/interviews",
    read: false,
    icon: Calendar,
    iconColor: "text-amber-400"
  });

  notifications.push({
    id: "notif-interview-completed-1",
    category: "Interviews",
    priority: "🟢 Success",
    title: "Senior Developer Panel Interview Completed",
    description: "Panel feedback recorded with 4.8/5 score. Candidate recommended for final decision round.",
    timestamp: "Yesterday, 17:30 PM",
    minutesAgo: 1300,
    timeGroup: "Yesterday",
    module: "Interview Workspace",
    navPath: "/admin/interviews",
    read: true,
    icon: CheckCircle2,
    iconColor: "text-emerald-400"
  });

  // 3. System & Intelligence Reports
  notifications.push({
    id: "notif-report-daily",
    category: "Reports",
    priority: "🔵 Information",
    title: "Human Capital Intelligence Daily Report Generated",
    description: "Daily PDF, HTML & JSON analytical report synthesized and archived in system repository.",
    timestamp: "Today, 06:00 AM",
    minutesAgo: 300,
    timeGroup: "Today",
    module: "Reports Archive",
    navPath: "/admin/reports-archive",
    read: false,
    icon: Archive,
    iconColor: "text-cyan-400"
  });

  notifications.push({
    id: "notif-system-diag",
    category: "System",
    priority: "🟢 Success",
    title: "Enterprise System Diagnostics Passed",
    description: "All endpoints, database pools, Graph API, and SMTP failover routing operating at 100% capacity.",
    timestamp: "Today, 08:00 AM",
    minutesAgo: 240,
    timeGroup: "Today",
    module: "System Diagnostics",
    navPath: "/admin/diagnostics",
    read: true,
    icon: Stethoscope,
    iconColor: "text-emerald-400"
  });

  // 4. Executive Alerts
  notifications.push({
    id: "notif-exec-health",
    category: "Executive",
    priority: "🟡 Medium",
    title: "Organization Recruitment Health Score: 94.5%",
    description: "Quantitative Research & Technology divisions operating with optimal candidate fill velocity.",
    timestamp: "This Week (Mon)",
    minutesAgo: 2800,
    timeGroup: "This Week",
    module: "Executive Overview",
    navPath: "/admin",
    read: true,
    icon: ShieldCheck,
    iconColor: "text-cyan-400"
  });

  notifications.push({
    id: "notif-exec-email-sys",
    category: "Communications",
    priority: "🟢 Success",
    title: "Microsoft Graph & SMTP Mail System Active",
    description: "Automated candidate communications and HR dispatch operating with 0 delivery failures.",
    timestamp: "This Week (Tue)",
    minutesAgo: 4000,
    timeGroup: "This Week",
    module: "Email Center",
    navPath: "/admin/emails",
    read: true,
    icon: Mail,
    iconColor: "text-emerald-400"
  });

  notifications.push({
    id: "notif-cron-schedule",
    category: "System",
    priority: "🔵 Information",
    title: "Automated Intelligence Cron Scheduler Running",
    description: "Daily (06:00 IST), Weekly (Mon 07:00 IST), and Monthly (1st 08:00 IST) triggers verified active.",
    timestamp: "Earlier",
    minutesAgo: 7200,
    timeGroup: "Earlier",
    module: "System Diagnostics",
    navPath: "/admin/diagnostics",
    read: true,
    icon: Zap,
    iconColor: "text-purple-400"
  });

  return notifications;
};

export const ExecutiveNotificationCenter = ({ applications = [], stats = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [activePriority, setActivePriority] = useState("All");
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-close drawer whenever route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.search]);

  // Close drawer on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Load / initialize notifications
  useEffect(() => {
    const saved = localStorage.getItem("quanentry_executive_notifications");
    if (saved) {
      try {
        setNotifications(JSON.parse(saved));
      } catch (e) {
        const generated = generateExecutiveNotifications(applications, stats);
        setNotifications(generated);
      }
    } else {
      const generated = generateExecutiveNotifications(applications, stats);
      setNotifications(generated);
    }
  }, [applications, stats]);

  // Persist state changes
  const saveState = (updated) => {
    setNotifications(updated);
    localStorage.setItem("quanentry_executive_notifications", JSON.stringify(updated));
  };

  const unreadCount = useMemo(() => {
    return notifications.filter(n => !n.read).length;
  }, [notifications]);

  // Filtering
  const filteredNotifications = useMemo(() => {
    return notifications.filter(n => {
      const matchCat = (activeCategory === "All") || 
        (activeCategory === "Unread" && !n.read) || 
        (n.category === activeCategory);

      const matchPrio = (activePriority === "All") || (n.priority.includes(activePriority));

      return matchCat && matchPrio;
    });
  }, [notifications, activeCategory, activePriority]);

  // Grouping into Today, Yesterday, This Week, Earlier
  const groupedNotifications = useMemo(() => {
    const groups = {
      "Today": [],
      "Yesterday": [],
      "This Week": [],
      "Earlier": []
    };

    filteredNotifications.forEach(n => {
      const groupKey = n.timeGroup || "Earlier";
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(n);
    });

    return groups;
  }, [filteredNotifications]);

  const toggleGroup = (groupKey) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const markAsRead = (id, e) => {
    if (e) e.stopPropagation();
    const updated = notifications.map(n => n.id === id ? { ...n, read: true } : n);
    saveState(updated);
  };

  const markAllAsRead = () => {
    const updated = notifications.map(n => ({ ...n, read: true }));
    saveState(updated);
  };

  const clearRead = () => {
    const updated = notifications.filter(n => !n.read);
    saveState(updated);
  };

  const handleBellToggle = () => {
    const nextState = !isOpen;
    console.debug(`[NOTIFICATION DRAWER ${nextState ? 'OPENED' : 'CLOSED'}]`, { unreadCount, timestamp: new Date().toISOString() });
    setIsOpen(nextState);
  };

  const handleNotificationClick = (notif) => {
    console.debug("[CLICK DETECTED: NOTIFICATION ITEM]", { id: notif.id, title: notif.title, navPath: notif.navPath, timestamp: new Date().toISOString() });
    console.debug("[HANDLER EXECUTED: NOTIFICATION CLICK]", { id: notif.id });
    markAsRead(notif.id);
    setIsOpen(false);
    if (notif.navPath) {
      console.debug("[NAVIGATE CALLED]", { targetRoute: notif.navPath });
      navigate(notif.navPath);
      console.debug("[ROUTE UPDATED]", { targetRoute: notif.navPath, newWindowPath: window.location.pathname });
    }
  };

  const priorityBadgeStyle = (priorityStr) => {
    if (priorityStr.includes("Critical")) return "bg-red-500/20 text-red-300 border-red-500/40";
    if (priorityStr.includes("High")) return "bg-amber-500/20 text-amber-300 border-amber-500/40";
    if (priorityStr.includes("Medium")) return "bg-yellow-500/15 text-yellow-300 border-yellow-500/30";
    if (priorityStr.includes("Information")) return "bg-cyan-500/20 text-cyan-300 border-cyan-500/40";
    return "bg-emerald-500/20 text-emerald-300 border-emerald-500/40";
  };

  const categories = ["All", "Unread", "Recruitment", "Interviews", "Communications", "Reports", "System", "Executive"];
  const priorities = ["All", "Critical", "High", "Medium", "Information", "Success"];

  return (
    <div className="relative inline-block" data-testid="executive-notifications-center">
      {/* Bell Trigger Button */}
      <button
        onClick={handleBellToggle}
        className="relative p-2.5 rounded-xl border border-cyan-500/30 bg-slate-900/80 hover:bg-cyan-400/10 hover:border-cyan-400/50 text-cyan-300 transition-all cursor-pointer shadow-lg shadow-cyan-500/10 flex items-center justify-center"
        title="Executive Notification Center"
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5 text-cyan-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white font-mono text-[10px] font-bold border-2 border-slate-950 shadow-md animate-pulse">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Slide-over Drawer / Popover Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />

            {/* Notification Drawer */}
            <motion.div
              initial={{ opacity: 0, x: 350 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 350 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed top-0 right-0 bottom-0 w-full sm:w-[480px] bg-slate-950 border-l border-cyan-500/30 shadow-2xl z-50 flex flex-col font-sans"
              data-testid="notification-panel-drawer"
            >
              {/* Header */}
              <div className="p-5 border-b border-white/10 bg-gradient-to-r from-slate-900 via-slate-950 to-cyan-950/40 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                    <Bell className="w-4 h-4 text-cyan-300" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white font-mono tracking-wider uppercase">Executive Notifications</h3>
                    <p className="text-[11px] text-cyan-300/70 font-mono">Live operational alert & intelligence feed</p>
                  </div>
                </div>

                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Action Bar */}
              <div className="px-5 py-3 border-b border-white/5 bg-slate-900/40 flex items-center justify-between text-xs font-mono">
                <div className="text-white/60">
                  <span className="text-cyan-300 font-bold">{unreadCount}</span> Unread Alerts
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={markAllAsRead}
                    className="text-cyan-300/80 hover:text-cyan-200 flex items-center gap-1 transition-colors cursor-pointer"
                  >
                    <CheckCheck className="w-3.5 h-3.5" /> Mark All Read
                  </button>
                  <button
                    onClick={clearRead}
                    className="text-white/40 hover:text-red-300 flex items-center gap-1 transition-colors cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Clear Read
                  </button>
                </div>
              </div>

              {/* Filter Pills */}
              <div className="p-4 border-b border-white/5 bg-black/30 space-y-2">
                {/* Category Filters */}
                <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1">
                  {categories.map((cat, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveCategory(cat)}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-mono transition-all whitespace-nowrap cursor-pointer ${
                        activeCategory === cat
                          ? 'bg-cyan-400/20 text-cyan-300 border border-cyan-400/40 font-semibold'
                          : 'bg-white/5 text-white/50 hover:text-white border border-transparent'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>

                {/* Priority Filters */}
                <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar text-[10px] font-mono">
                  <span className="text-white/30 uppercase mr-1">Priority:</span>
                  {priorities.map((prio, i) => (
                    <button
                      key={i}
                      onClick={() => setActivePriority(prio)}
                      className={`px-2 py-0.5 rounded text-[10px] transition-all whitespace-nowrap cursor-pointer ${
                        activePriority === prio
                          ? 'bg-amber-400/20 text-amber-300 border border-amber-400/40 font-semibold'
                          : 'bg-white/5 text-white/40 hover:text-white border border-transparent'
                      }`}
                    >
                      {prio}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notification List Scrollable */}
              <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
                {Object.keys(groupedNotifications).map((groupKey) => {
                  const items = groupedNotifications[groupKey];
                  if (!items || items.length === 0) return null;
                  const isCollapsed = collapsedGroups[groupKey];

                  return (
                    <div key={groupKey} className="space-y-2">
                      {/* Group Header */}
                      <button
                        onClick={() => toggleGroup(groupKey)}
                        className="w-full flex items-center justify-between text-xs font-mono uppercase tracking-wider text-cyan-300/80 bg-white/5 px-3 py-1.5 rounded-lg border border-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                      >
                        <span className="flex items-center gap-2">
                          <ChevronRight className={`w-3.5 h-3.5 transition-transform ${!isCollapsed ? 'rotate-90' : ''}`} />
                          {groupKey}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-black/40 text-white/60">
                          {items.length}
                        </span>
                      </button>

                      {/* Group Items */}
                      {!isCollapsed && (
                        <div className="space-y-2 pt-1">
                          {items.map((notif) => {
                            const IconComponent = notif.icon || Bell;

                            return (
                              <div
                                key={notif.id}
                                onClick={() => handleNotificationClick(notif)}
                                className={`p-3.5 rounded-xl border transition-all cursor-pointer relative group ${
                                  !notif.read
                                    ? 'bg-gradient-to-r from-slate-900/90 to-cyan-950/30 border-cyan-500/40 shadow-sm shadow-cyan-500/10'
                                    : 'bg-black/30 border-white/5 hover:bg-white/5'
                                }`}
                              >
                                {!notif.read && (
                                  <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
                                )}

                                <div className="flex items-start gap-3">
                                  <div className={`p-2 rounded-lg bg-white/5 border border-white/10 flex-shrink-0 mt-0.5 ${notif.iconColor || 'text-cyan-300'}`}>
                                    <IconComponent className="w-4 h-4" />
                                  </div>

                                  <div className="flex-1 min-w-0 pr-4">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                      <span className={`px-2 py-0.5 rounded text-[9px] font-mono font-semibold border uppercase ${priorityBadgeStyle(notif.priority)}`}>
                                        {notif.priority}
                                      </span>
                                      <span className="text-[10px] font-mono text-white/40">{notif.category}</span>
                                    </div>

                                    <div className="text-xs font-semibold text-white group-hover:text-cyan-300 transition-colors leading-snug">
                                      {notif.title}
                                    </div>

                                    <div className="text-[11px] text-white/70 mt-1 leading-relaxed line-clamp-2 font-sans">
                                      {notif.description}
                                    </div>

                                    <div className="mt-2.5 flex items-center justify-between text-[10px] font-mono text-white/40">
                                      <span>{notif.timestamp}</span>
                                      <span className="text-cyan-300/80 hover:underline flex items-center gap-0.5">
                                        Open {notif.module} <ChevronRight className="w-3 h-3" />
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

                {filteredNotifications.length === 0 && (
                  <div className="p-8 text-center text-xs font-mono text-white/40">
                    No notifications match current filter criteria.
                  </div>
                )}
              </div>

              {/* Drawer Footer */}
              <div className="p-4 border-t border-white/10 bg-slate-950 text-center text-[10px] font-mono text-white/30">
                Enterprise Executive Intelligence Platform • Version 1.1 Freeze
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ExecutiveNotificationCenter;
